import * as React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiMoreHorizontal } from 'react-icons/fi';
import { cn } from '@/lib/utils';
import { useSession } from '@/hooks/useSession';
import { checkPermission } from '@/hooks/usePermission';
import { usePlatformAbilities } from '@/hooks/usePlatformAbilities';
import { adminMenu } from '@/components/custom/menu/menu-admin';
// O chat é o mesmo da T.I.A. por baixo, mas para o professor quem aparece é o
// personagem-professor — é ele que fala com o docente no restante da área
// (hero do dashboard, PROF-*). Avatar recortado de PROF-JOINHA.png no mesmo
// formato do da T.I.A. (256x256, rosto centralizado para caber em círculo).
import profAvatar from '@/assets/images/prof/prof-avatar-3d.png';
import { TiaChatBar } from '@/components/custom/bottom-bar-infantil/tia-chat-bar';
import { TiaChatSheet } from '@/components/custom/bottom-bar-infantil/tia-chat-sheet';
import { useTiaChat, getUserLearningPathIdFromPathname } from '@/components/custom/bottom-bar-infantil/use-tia-chat';
import { usePresence } from '@/components/custom/bottom-bar-infantil/bottom-bar-infantil';
import { Tooltip as ReactTooltip } from 'react-tooltip';

const MOBILE_LEFT_TABS = 3;
const MOBILE_RIGHT_TABS = 2;

// Mesma geometria da barra do aluno: largura igual por aba (flex-1) para que
// trocar de ativa não desloque o layout; no mobile só ícone.
const tabClassName = (isActive, hideOnMobile) =>
  cn(
    'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-full px-1 py-1.5 text-center text-[11px] font-semibold transition-colors max-[430px]:text-[10px]',
    isActive ? 'text-[var(--color-mission-dark)]' : 'text-neutral-dark',
    hideOnMobile && 'max-sm:hidden'
  );

const tabStyle = isActive => (isActive ? { background: 'color-mix(in srgb, var(--color-mission) 16%, #fff)' } : undefined);

// Ícones do adminMenu vêm como elemento já criado (<FiHome />), não como
// componente — por isso são clonados via wrapper com tamanho forçado em vez
// de receberem prop size.
const renderIcon = icon => <span className="[&>svg]:h-[17px] [&>svg]:w-[17px] sm:[&>svg]:h-5 sm:[&>svg]:w-5">{icon}</span>;

// Mesma regra de truncate+tooltip da barra do aluno (NavTab em
// bottom-bar-infantil.jsx): título comprido detectado via ResizeObserver
// (scrollWidth > clientWidth) só ganha tooltip quando realmente corta —
// sem isso os títulos do professor sobrepunham na raia estreita de cada aba.
function ProfessorNavTab({ tab, isActive, hideOnMobile, onNavigate }) {
  const labelRef = React.useRef(null);
  const [truncated, setTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = labelRef.current;
    if (!el) return undefined;

    const checkTruncation = () => setTruncated(el.scrollWidth > el.clientWidth);

    checkTruncation();
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tab.label]);

  const tooltipId = `navtab-tooltip-${tab.key}`;

  return (
    <Link
      to={tab.to}
      onClick={onNavigate}
      aria-current={isActive ? 'page' : undefined}
      // No mobile o texto abaixo some por CSS (max-sm:hidden) e display:none
      // apaga o nome acessível junto — leitor de tela anunciaria só "link".
      // O aria-label garante o nome nos dois tamanhos, independente de CSS.
      aria-label={tab.label}
      className={tabClassName(isActive, hideOnMobile)}
      style={tabStyle(isActive)}
      data-tooltip-id={truncated ? tooltipId : undefined}
      data-tooltip-content={truncated ? tab.label : undefined}
    >
      {renderIcon(tab.icon)}
      <span
        ref={labelRef}
        className="block max-w-full truncate leading-none max-sm:hidden"
      >
        {tab.label}
      </span>
      {truncated && (
        <ReactTooltip
          id={tooltipId}
          className="tooltip"
          place="top"
        />
      )}
    </Link>
  );
}

// A barra do professor NÃO tem lista própria de destinos: ela projeta o mesmo
// adminMenu que alimenta a sidebar. Assim permissão nova/rota nova aparece nos
// dois lugares de uma vez, sem segunda lista para esquecer de atualizar.
//
// Os grupos são achatados: cada destino visível vira uma aba própria, como na
// barra do aluno. Título de grupo ("Organização da Plataforma") não diz ao
// professor o que existe lá dentro, e o grupo não era navegável — custava um
// toque a mais para chegar em qualquer lugar. Cada folha do adminMenu já traz
// icon e title próprios, então achatar não perde ícone nem rótulo.
//
// Regra de visibilidade copiada de menu-item.jsx:33 e submenu.jsx:218 — entram
// só os destinos visíveis; um grupo inteiro invisível some por consequência,
// sem precisar de regra separada.
export function buildProfessorTabs(menu = []) {
  return (menu || []).reduce((tabs, item) => {
    // Item raiz sem submenu (Home) vira link direto.
    if (!item?.submenu) {
      if (item?.route) tabs.push({ key: `route:${item.route}`, label: item.label, icon: item.icon, to: item.route, activeMatch: item.activeMatch });
      return tabs;
    }

    (item.submenu.nav || []).filter(navItem => navItem?.visible && navItem?.route).forEach(navItem => tabs.push({ key: `route:${navItem.route}`, label: navItem.title, icon: navItem.icon, to: navItem.route, activeMatch: navItem.activeMatch }));

    return tabs;
  }, []);
}

// Núcleo do professor: o que ele abre toda semana, na ordem dos slots do mobile
// (3 à esquerda da T.I.A., 2 à direita). Casa por rota e não por título porque
// título muda com revisão de copy e rota não.
export const PRIMARY_ROUTES = ['/', '/turmas', '/plano-de-aulas/inicio', '/redacao', '/metricas/institucional'];

// A ordem do adminMenu é de cadastro, não de importância — sem isto os slots do
// mobile eram decididos por posição no menu, e sobrava "Seletivos" na barra com
// "Turmas" escondida no "Mais". Núcleo primeiro, na ordem acima; o resto mantém
// a ordem do menu. Destino do núcleo que a permissão escondeu não vira buraco:
// ele some da lista e as secundárias sobem para os slots livres.
export function splitByPriority(tabs = []) {
  const primaryTabs = PRIMARY_ROUTES.map(route => (tabs || []).find(tab => tab.to === route)).filter(Boolean);
  const secondaryTabs = (tabs || []).filter(tab => !PRIMARY_ROUTES.includes(tab.to));

  return { primaryTabs, secondaryTabs };
}

// Provas e Simulados vivem na mesma tela agora (toggle interno, ver
// src/pages/exams/PlayfulTeacher/AssessmentsToggle.jsx) — a bottom bar do
// professor mostra as duas como um destino só, "Avaliações". Se só Provas
// estiver visível (perfil só-professor, Simulados escondido em
// menu-admin.jsx), a mesma aba vira "Avaliações" apontando só pra ela.
// activeMatch lista os prefixos exatos das duas rotas (+ resultado de prova,
// que vive fora de '/avaliacoes') em vez do prefixo genérico '/avaliacoes' —
// esse prefixo casava com QUALQUER subrota de avaliações, então "Questões"
// (/avaliacoes/banco-de-questoes) e "Exercícios" (/avaliacoes/apostilas)
// acendiam junto com "Avaliações" ao serem selecionados.
export function mergeAssessmentsTabs(tabs = []) {
  const provasTab = tabs.find(tab => tab.to === '/avaliacoes/provas');
  if (!provasTab) return tabs;

  const merged = { ...provasTab, key: 'route:/avaliacoes', label: 'Avaliações', activeMatch: ['/avaliacoes/provas', '/avaliacoes/simulados', '/resultados-prova'] };
  return [merged, ...tabs.filter(tab => tab.to !== '/avaliacoes/provas' && tab.to !== '/avaliacoes/simulados')];
}

// Mesma detecção de rota ativa da sidebar (menu-item.jsx:25-26): prefixo com
// barra para '/turmas' não acender em '/turmas-arquivadas'. '/' exige igualdade
// exata, senão a Home ficaria sempre ativa. activeMatch aceita string ou lista
// de prefixos — "Avaliações" precisa de mais de um (ver mergeAssessmentsTabs).
export function isTabActive(tab, pathname) {
  const matchBases = [].concat(tab?.activeMatch || tab?.to || []);
  return matchBases.some(matchBase => {
    if (!matchBase) return false;
    if (matchBase === '/') return pathname === '/';
    return pathname === matchBase || pathname.startsWith(`${matchBase}/`);
  });
}

export function BottomBarProfessor() {
  const { session } = useSession();
  const { abilities } = usePlatformAbilities();
  const { pathname } = useLocation();

  const tabs = React.useMemo(() => mergeAssessmentsTabs(buildProfessorTabs(adminMenu({ session, abilities, checkPermission }))), [session, abilities]);

  // Divisão simétrica em torno da T.I.A.: o núcleo ocupa os slots visíveis no
  // mobile e as secundárias completam as laterais no desktop. Com permissão
  // restrita sobram menos abas e a barra encolhe sozinha.
  // A cota do mobile é preenchida na ordem de prioridade ANTES de dividir os
  // lados: só desce para o "Mais" o que não coube nos slots. Dividir primeiro e
  // cortar depois enterrava aba tendo slot livre do outro lado — com permissão
  // restrita o núcleo não enchia a esquerda, a primeira metade das secundárias
  // ficava além da cota e caía no "Mais" com a direita ainda vazia.
  const { primaryTabs, secondaryTabs } = splitByPriority(tabs);
  const ordered = [...primaryTabs, ...secondaryTabs];
  const mobileLeft = ordered.slice(0, MOBILE_LEFT_TABS);
  const mobileRight = ordered.slice(MOBILE_LEFT_TABS, MOBILE_LEFT_TABS + MOBILE_RIGHT_TABS);
  // O que excede a cota só aparece no desktop (max-sm:hidden), repartido entre
  // os dois lados; no mobile essas mesmas abas são o conteúdo do "Mais".
  const desktopRest = ordered.slice(MOBILE_LEFT_TABS + MOBILE_RIGHT_TABS);
  const restMiddle = Math.ceil(desktopRest.length / 2);
  const leftTabs = [...mobileLeft, ...desktopRest.slice(0, restMiddle)];
  const rightTabs = [...mobileRight, ...desktopRest.slice(restMiddle)];
  const overflowTabs = [...leftTabs.slice(MOBILE_LEFT_TABS), ...rightTabs.slice(MOBILE_RIGHT_TABS)];
  const moreActive = overflowTabs.some(tab => isTabActive(tab, pathname));

  const [moreOpen, setMoreOpen] = React.useState(false);
  const [chatOpen, setChatOpen] = React.useState(false);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const barPresence = usePresence(chatOpen);
  const sheetPresence = usePresence(sheetOpen);
  const chat = useTiaChat({ userLearningPathId: getUserLearningPathIdFromPathname(pathname), currentPath: pathname });
  const hasLoadedHistory = React.useRef(false);

  // Troca de rota fecha o sheet de "Mais" — senão ele fica aberto por cima da
  // página recém-navegada.
  React.useEffect(() => {
    setMoreOpen(false);
  }, [pathname]);

  const closeChat = () => {
    setChatOpen(false);
    setSheetOpen(false);
  };

  const openChat = () => {
    setMoreOpen(false);
    setChatOpen(prev => {
      const next = !prev;
      if (!next) setSheetOpen(false);
      return next;
    });
    if (!hasLoadedHistory.current) {
      hasLoadedHistory.current = true;
      chat.loadHistory();
    }
  };

  // Toda aba é link direto agora; navegar só precisa fechar o sheet de "Mais".
  const closeMore = () => setMoreOpen(false);

  const renderTab = (tab, hideOnMobile) => (
    <ProfessorNavTab
      key={tab.key}
      tab={tab}
      isActive={isTabActive(tab, pathname)}
      hideOnMobile={hideOnMobile}
      onNavigate={closeMore}
    />
  );

  return (
    <>
      {sheetPresence.shouldRender && (
        <TiaChatSheet
          messages={chat.messages}
          status={chat.status}
          onRetry={chat.retry}
          onClose={() => setSheetOpen(false)}
          closing={sheetPresence.closing}
        />
      )}
      <div className="fixed bottom-[calc(1.75rem+env(safe-area-inset-bottom))] left-1/2 z-[103] w-[calc(100%-1rem)] max-w-xl -translate-x-1/2 sm:w-[calc(100%-2rem)] md:max-w-3xl">
        <nav
          className={cn('c-bottom-bar-professor', 'flex items-stretch', 'rounded-full border border-neutral-lighter bg-white px-2 py-1.5', 'shadow-[0_12px_32px_rgba(0,0,0,0.16)]')}
          aria-label="Navegação principal"
        >
          {leftTabs.map((tab, i) => renderTab(tab, i >= MOBILE_LEFT_TABS))}
          {abilities?.['tia-chat'] && (
            <button
              type="button"
              onClick={openChat}
              aria-label="Conversar com a T.I.A"
              aria-expanded={chatOpen}
              aria-controls="tia-chat-sheet"
              className="flex w-10 flex-none items-start justify-center sm:w-auto sm:flex-1"
            >
              <img
                src={profAvatar}
                alt=""
                className={cn('-mt-8 h-16 w-16 max-w-none shrink-0 rounded-full border-4 border-white bg-white shadow-lg', 'transition-transform active:scale-95', chatOpen && 'ring-2 ring-secondary')}
              />
            </button>
          )}
          {rightTabs.map((tab, i) => renderTab(tab, i >= MOBILE_RIGHT_TABS))}
          {overflowTabs.length > 0 && (
            <button
              type="button"
              onClick={() => setMoreOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={moreOpen}
              // O texto abaixo é sempre display:none (class 'hidden'), então
              // não serve de nome acessível: sem isto o botão vira só "button".
              aria-label="Ver mais"
              className={cn(
                'flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-full px-1 py-1.5 text-center text-[11px] font-semibold transition-colors max-[430px]:text-[10px] sm:hidden',
                moreActive || moreOpen ? 'text-[var(--color-mission-dark)]' : 'text-neutral-dark'
              )}
              style={tabStyle(moreActive || moreOpen)}
            >
              <FiMoreHorizontal
                aria-hidden="true"
                className="h-[17px] w-[17px]"
              />
              <span className="hidden">Ver mais</span>
            </button>
          )}
        </nav>
        {barPresence.shouldRender && (
          <TiaChatBar
            onSubmit={body => {
              setSheetOpen(true);
              chat.send(body);
            }}
            onFocus={() => {
              if (chat.messages.length > 0) setSheetOpen(true);
            }}
            onClose={closeChat}
            disabled={chat.status === 'thinking' || chat.status === 'loading'}
            closing={barPresence.closing}
          />
        )}
      </div>

      {/* "Ver mais": abas que não couberam na cota mobile. Todas são links
          diretos — o sheet fecha na navegação, sem segundo nível. */}
      {moreOpen && (
        <>
          <div
            className="fixed inset-0 z-[110] bg-black/30"
            onClick={() => setMoreOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Mais acessos"
            className="fixed inset-x-0 bottom-0 z-[111] rounded-t-3xl bg-white px-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-12px_32px_rgba(0,0,0,0.16)]"
          >
            <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-neutral-lighter" />
            <p className="mb-4 text-sm font-bold text-neutral-dark">Mais acessos</p>
            <div className="grid grid-cols-3 gap-3">
              {overflowTabs.map(tab => {
                const isActive = isTabActive(tab, pathname);
                const className = cn(
                  'flex flex-col items-center gap-2 rounded-2xl border px-2 py-4 text-center text-[12px] font-semibold transition-colors',
                  isActive ? 'border-mission/30 text-[var(--color-mission-dark)]' : 'border-neutral-lighter text-neutral-dark'
                );
                const style = isActive ? { background: 'color-mix(in srgb, var(--color-mission) 12%, #fff)' } : undefined;

                return (
                  <Link
                    key={tab.key}
                    to={tab.to}
                    onClick={closeMore}
                    aria-current={isActive ? 'page' : undefined}
                    className={className}
                    style={style}
                  >
                    <span className="[&>svg]:h-[22px] [&>svg]:w-[22px]">{tab.icon}</span>
                    <span className="leading-none">{tab.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
